import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  ShieldCheck, XCircle, CheckCircle2, Loader2, IndianRupee, BedDouble,
  CalendarDays, User, MapPin, Utensils, Snowflake,
} from "lucide-react";

/**
 * Public booking verification page (no login required).
 * Hostel owners open this at check-in to confirm a student's booking is genuinely paid.
 * Prevents fake screenshots being accepted as proof of payment.
 */
export default function VerifyBooking() {
  const [params, setParams] = useSearchParams();
  const [code, setCode] = useState((params.get("code") || "").toUpperCase());
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // null | {valid:true,...} | {error}

  const doVerify = async (codeToCheck) => {
    const c = (codeToCheck || code || "").trim().toUpperCase();
    if (!c) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get(`/verify/${encodeURIComponent(c)}`);
      setResult(data);
      setParams({ code: c }, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || "Verification failed";
      setResult({ error: detail });
    } finally { setLoading(false); }
  };

  // Auto-verify if ?code= is present in the URL on load
  useEffect(() => {
    const q = (params.get("code") || "").toUpperCase();
    if (q) doVerify(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => { e.preventDefault(); doVerify(); };

  return (
    <div className="min-h-screen bg-[#FBF5EA]" data-testid="verify-booking-page">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="text-xs tracking-[0.25em] uppercase font-bold text-amber-600">For hostel owners</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-1">Verify a booking</h1>
        <p className="mt-3 text-slate-600 max-w-lg">
          Enter the 10-character code the student shows you (starts with <span className="font-mono">HN-</span>).
          Only genuinely paid bookings will return a match — screenshots or fake codes will be rejected.
        </p>

        <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3">
          <Input
            data-testid="verify-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="HN-XXXXX-XXXXX"
            className="h-12 font-mono tracking-widest text-lg uppercase"
            autoFocus
          />
          <Button
            type="submit"
            data-testid="verify-submit-btn"
            disabled={loading || !code.trim()}
            className="h-12 px-6 rounded-full bg-orange-800 hover:bg-orange-900 text-white active:scale-95 transition-transform gap-2"
          >
            {loading ? <><Loader2 className="animate-spin" size={16} /> Checking…</> : <><ShieldCheck size={16} /> Verify</>}
          </Button>
        </form>

        {/* Result states */}
        {result?.error && (
          <div data-testid="verify-invalid" className="mt-8 rounded-2xl border-2 border-rose-300 bg-rose-50 p-6 flex items-start gap-4">
            <XCircle className="text-rose-600 flex-shrink-0" size={32} />
            <div>
              <div className="font-display font-black text-2xl text-rose-800">Invalid or unpaid</div>
              <p className="mt-2 text-sm text-rose-900">{result.error}</p>
              <p className="mt-3 text-xs text-rose-800/80">Do NOT accept this booking as proof of payment. Ask the student to complete their Razorpay payment first.</p>
            </div>
          </div>
        )}

        {result?.valid && (
          <div data-testid="verify-valid" className="mt-8 rounded-2xl border-2 border-emerald-400 bg-emerald-50 overflow-hidden">
            <div className="bg-emerald-600 text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2 font-bold">
                <CheckCircle2 size={20} /> Genuine paid booking
              </div>
              <span className="text-[10px] uppercase tracking-widest font-black bg-white/20 px-2 py-1 rounded-full">Verified live from HostelNow</span>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Code</div>
              <div className="font-mono font-black text-2xl tracking-widest text-emerald-900 -mt-3">{result.verification_code}</div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-emerald-200">
                <Row icon={User} label="Guest" value={result.guest_name || "—"} />
                <Row icon={MapPin} label="Hostel" value={`${result.hostel_name} · ${result.hostel_city}`} />
                <Row icon={CalendarDays} label="Check-in → Check-out" value={`${result.check_in} → ${result.check_out} (${result.nights} nights)`} />
                <Row icon={BedDouble} label="Beds & room" value={`${result.beds} bed(s) · ${result.room_type || "Non-A/C"}`} />
                <Row icon={IndianRupee} label="Amount paid" value={`₹${(result.amount || 0).toLocaleString("en-IN")}`} />
                <Row icon={result.meal_included ? Utensils : Snowflake} label="Add-ons" value={result.meal_included ? "Meals included" : "No meals"} />
              </div>

              <div className="pt-4 border-t border-emerald-200 flex flex-col sm:flex-row justify-between gap-2 text-xs text-emerald-900">
                <span>Razorpay reference: <span className="font-mono">{result.payment_ref}</span></span>
                <span>Paid at: {result.paid_at?.slice(0, 19).replace("T", " ") || "—"}</span>
              </div>
            </div>
          </div>
        )}

        <div className="mt-10 rounded-xl bg-white border border-slate-200 p-4 text-xs text-slate-600 leading-relaxed">
          <strong className="text-slate-900">How this works:</strong> When a student completes payment via Razorpay, HostelNow generates a unique server-side verification code that cannot be faked or reproduced from screenshots. This live-verified page is the only proof a booking is genuine. If the code isn't valid here, no money was actually received.
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={14} className="text-emerald-700 flex-shrink-0 mt-1" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">{label}</div>
        <div className="text-sm text-emerald-950 truncate">{value}</div>
      </div>
    </div>
  );
}
